import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext(null);
const CACHE_NAME = 'khills-data-v1';
const DATA_PATH = process.env.PUBLIC_URL
  ? `/${process.env.PUBLIC_URL.replace(/^\/+|\/+$/g, '')}/data`
  : '/data';
const FETCH_HEADERS = { 'X-App-Request': 'khills' };

const DATA_FILES = [
  'sandha.json',
  'products.json',
  'address.json',
  'masterAddress.json',
  'ivr.json',
  'postalRejectReason.json',
  'pageNos.json',
];

function getAppState(time) {
  const bypassCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('bypass='))
    ?.split('=')[1];
  if (bypassCookie === 'true') return 'active';

  const params = new URLSearchParams(window.location.search);
  if (params.get('bypass') === 'true') return 'active';

  if (time.festivalDate) {
    const festival = new Date(time.festivalDate);
    festival.setDate(festival.getDate() + 6);
    if (new Date().getTime() >= festival.getTime()) return 'completed';
  }

  return 'active';
}

async function fetchJSON(url) {
  const res = await fetch(url, { headers: FETCH_HEADERS });
  if (!res.ok) throw new Error(`Failed to fetch ${url}`);
  return res.json();
}

async function loadData() {
  const cache = await caches.open(CACHE_NAME);
  const manifestUrl = `${DATA_PATH}/manifest.json`;

  let manifest;
  try {
    manifest = await fetchJSON(manifestUrl);
  } catch {
    manifest = null;
  }

  const cachedManifest = await cache.match(manifestUrl);
  const cachedVersion = cachedManifest
    ? (await cachedManifest.json()).version
    : null;

  const needsRefresh =
    !cachedVersion || !manifest || cachedVersion !== manifest.version;

  // Always fetch time.json first to determine app state
  const timeUrl = `${DATA_PATH}/time.json`;
  let time;

  if (needsRefresh || !(await cache.match(timeUrl))) {
    time = await fetchJSON(timeUrl);
  } else {
    const cachedTime = await cache.match(timeUrl);
    time = await cachedTime.json();
  }

  const appState = getAppState(time);

  // If function completed, only return time data — skip donor files
  if (appState === 'completed') {
    // Cache manifest and time so next load serves from cache
    if (needsRefresh && manifest) {
      await caches.delete(CACHE_NAME);
      const freshCache = await caches.open(CACHE_NAME);
      await freshCache.put(
        manifestUrl,
        new Response(JSON.stringify(manifest), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
      await freshCache.put(
        timeUrl,
        new Response(JSON.stringify(time), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
    }
    return { time, config: time?.config || {}, _appState: 'completed' };
  }

  // App is active — fetch all data files
  if (needsRefresh && manifest) {
    await caches.delete(CACHE_NAME);
    const freshCache = await caches.open(CACHE_NAME);

    await freshCache.put(
      manifestUrl,
      new Response(JSON.stringify(manifest), {
        headers: { 'Content-Type': 'application/json' },
      })
    );

    const data = { time };
    await freshCache.put(
      timeUrl,
      new Response(JSON.stringify(time), {
        headers: { 'Content-Type': 'application/json' },
      })
    );

    await Promise.all(
      DATA_FILES.map(async (file) => {
        const url = `${DATA_PATH}/${file}`;
        const json = await fetchJSON(url);
        data[file.replace('.json', '')] = json;
        await freshCache.put(
          url,
          new Response(JSON.stringify(json), {
            headers: { 'Content-Type': 'application/json' },
          })
        );
      })
    );
    data.config = data.time?.config || {};
    return data;
  }

  // Load from cache
  const data = { time };
  let allCached = true;
  await Promise.all(
    DATA_FILES.map(async (file) => {
      const url = `${DATA_PATH}/${file}`;
      const cached = await cache.match(url);
      if (cached) {
        data[file.replace('.json', '')] = await cached.json();
      } else {
        allCached = false;
      }
    })
  );

  if (allCached) {
    data.config = data.time?.config || {};
    return data;
  }

  // Fallback: fetch all
  await Promise.all(
    DATA_FILES.map(async (file) => {
      const url = `${DATA_PATH}/${file}`;
      const json = await fetchJSON(url);
      data[file.replace('.json', '')] = json;
      await cache.put(
        url,
        new Response(JSON.stringify(json), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
    })
  );
  data.config = data.time?.config || {};
  return data;
}

export function DataProvider({ children }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const loading = React.useRef(false);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    loadData().then(setData).catch(setError);
  }, []);

  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100">
        <div className="text-center text-danger">
          <p>Failed to load data. Please refresh.</p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-2" role="status"></div>
          <p className="text-muted small">Loading...</p>
        </div>
      </div>
    );
  }

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
